import { useContext, useEffect } from "react";

import { Classes, Colors } from "@blueprintjs/core";
import { useHead } from "@unhead/react";
import { Link, useLocation, useParams, useSearchParams } from "react-router";

import { useCurrentSession } from "@/components/helpers/custom-hooks/use-current-session";
import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { useStoreSignUpParams } from "@/components/helpers/custom-hooks/use-store-signup-params";
import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { getSignUpParams } from "@/components/helpers/signup/sign-up-params";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { Nav } from "@/components/nav/Nav";
import { ClientComments } from "@/components/pages_flexible/coach/ClientComments";
import {
  CoachProfileHeader,
  CoachProfileSkeleton,
  CoachProfileVideo,
} from "@/components/pages_flexible/coach/CoachProfile";
import { ProfileDetails } from "@/components/pages_flexible/coach/ProfileDetails";
import { BigButton } from "@/components/pages_flexible/explore/styles";
import { PageContainer } from "@/components/pages_flexible/PageContainer";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { NotFound } from "@/components/pages_public/404";
import { AlertMessage } from "@/components/pieces/coaching/coach-profile/AlertMessage";
import { Specialties } from "@/components/pieces/coaching/coach-profile/CoachingProducts/Specialties";
import { ResponseContext } from "@/components/ResponseContext";
import { getUser } from "@/components/session/JwtTokenManager";
import { PUBLIC_CONTENT_MAX_WIDTH, SIDE_NAV_WIDTH } from "@/css/constants";
import {
  useGetAgentProfileQuery,
  useGetAllCoachProductsQuery,
  useGetAvailableCoachesQuery,
} from "@/graphql";
import { AgentProfile } from "@/types/coaches";
import { Specialty } from "@/types/products";

const FLOATING_PADDING = [0, 50, 75, 100];
const FLOATING_WIDTH = [0, 250, 375];
const FLOATING_FULL_WIDTH_NO_SIDEBAR = [0, 0, 450, 475];
const FLOATING_FULL_WIDTH_SIDEBAR = [0, 0, 0, 475];

export const categoryTitle = {
  MEETING: "1:1 VIDEO SESSIONS",
  REVIEW: "OTHER SERVICES",
};

function generateSpecialtiesSubstring(specialties: Specialty[]) {
  if (!specialties || !specialties.length) return "-";
  else if (specialties.length === 1) {
    return `- ${specialties[0].name} -`;
  } else if (specialties.length === 2) {
    return `- ${specialties[0].name} and ${specialties[1].name} -`;
  } else {
    return `- ${specialties[0].name}, ${specialties[1].name}, more -`;
  }
}

export function generateCoachSeoData(
  profile: AgentProfile,
  specialties: Specialty[],
) {
  const title = profile?.user?.name || "Coach Profile";
  const description = profile?.summary || "Coach profile";
  const specialtiesSubString = generateSpecialtiesSubstring(specialties);

  const coachData = {
    seoTitle: `${title} ${specialtiesSubString} ${
      IS_EXEC ? "Exec" : "Placement"
    }`,
    seoDescription: description,
  };

  return coachData;
}

interface CoachProfilePageProps {
  isAssignedCoach: boolean;
  hasSidebar?: boolean;
}

function CoachProfilePage({
  isAssignedCoach = false,
  hasSidebar = false,
}: CoachProfilePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { res } = useContext(ResponseContext);
  const currentUser = getUser();
  const { storeParams } = useStoreSignUpParams();

  let { coachSlug, specialtySlug } = useParams();
  coachSlug = coachSlug?.replace(/^@/, "").toLowerCase();
  specialtySlug = specialtySlug?.toLowerCase();

  const FLOATING_FULL_WIDTH = hasSidebar
    ? FLOATING_FULL_WIDTH_SIDEBAR
    : FLOATING_FULL_WIDTH_NO_SIDEBAR;

  const {
    data,
    loading,
    error: agentProfileError,
    refetch,
  } = useGetAgentProfileQuery({
    variables: {
      coachSlug,
      specialtySlug,
    },
  });

  const profile = data?.agentProfile;

  const { data: productsData, loading: productsLoading } =
    useGetAllCoachProductsQuery({
      variables: {
        coachId: profile?.user?.id || "",
      },
      skip: !profile?.user?.id,
    });

  const specialties = productsData?.productsByCoachId.specialties || [];
  const coachProducts = productsData?.productsByCoachId.coachProducts || [];

  useEffect(() => {
    if (profile?.user) {
      if (profile.user.referralCode) {
        maybeAttributeReferralCode();
      }
    }
  }, [profile]);

  useHead({
    bodyAttrs: { class: "public-site" },
  });

  const maybeAttributeReferralCode = () => {
    const signUpParams = getSignUpParams("", "", "");

    if (signUpParams.firstPage === location.pathname && !signUpParams.refcode) {
      // Create new search params with referral code
      searchParams.set("refcode", profile?.user?.referralCode || "");

      // Update URL with new search params
      setSearchParams(searchParams);

      // Store updated params
      storeParams();
    }
  };

  if (loading) {
    return (
      <>
        <Box mt={4} className={Classes.SKELETON} height={100} />
      </>
    );
  }

  if (!profile) {
    if (res) {
      res.status(404);
    }
    return <NotFound />;
  }

  const { seoTitle, seoDescription } = generateCoachSeoData(
    profile,
    specialties,
  );

  const hideBookMeetings =
    !profile.acceptingNewBookings ||
    (!profile.acceptingNewClients && !isAssignedCoach) ||
    (!productsLoading && specialties.length === 0);

  const specialtyId = specialties.find(
    (specialty) => specialty.slug === specialtySlug,
  )?.specialtyId;

  const getBookingLink = () => {
    const url = specialtyId
      ? `/schedule/${profile?.user?.id}?specialtyId=${specialtyId}`
      : `/schedule/${profile?.user?.id}`;

    if (!currentUser) {
      return `/start?nextPath=${url}`;
    }
    return url;
  };

  const bookingCoachBox = (
    <div className="mt-8 bg-white p-4 rounded-lg">
      {hideBookMeetings ? (
        <AlertMessage
          name={profile?.user?.name}
          message={`is not accepting new clients right now`}
        />
      ) : (
        <>
          <div className="text-xl font-bold mb-4 font-serif">
            Schedule Session
          </div>
          <Link to={getBookingLink()}>
            <BigButton intent="primary" large={true} fill={true}>
              Book Now
            </BigButton>
          </Link>
        </>
      )}
    </div>
  );

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalDomain={
          !profile
            ? undefined
            : profile.primarySite === "EXEC"
              ? "https://www.exec.com"
              : "https://www.placement.com"
        }
        canonicalPath={`/@${coachSlug}`}
        image={profile && profile.seoPhoto}
        additionalHeadTags={
          profile && !profile.seoIndex ? (
            <meta name="robots" content="noindex" />
          ) : null
        }
      />
      <Box id="sticky-container" css={{ position: "relative" as const }}>
        <Flex
          flexDirection="column"
          alignItems="center"
          css={{
            background:
              "linear-gradient(99.62deg, #003881 33.88%, #2970C9 98.39%)",
          }}
          px={[3, 4]}
          pt={4}
          pb={2}
        >
          <Flex
            width={1}
            maxWidth={PUBLIC_CONTENT_MAX_WIDTH}
            justifyContent="space-between"
            alignItems="self-start"
          >
            <Box pr={FLOATING_PADDING}>
              {profile ? (
                <CoachProfileHeader
                  profile={profile}
                  specialtySlug={specialtySlug}
                  containerCss={{
                    maxWidth: "100%",
                    borderRadius: 0,
                    color: Colors.WHITE,
                  }}
                  isProfile={true}
                  onSave={async () => {
                    await refetch();
                  }}
                  bgColor="transparent"
                />
              ) : (
                loading && (
                  <CoachProfileSkeleton
                    isFullMode={false}
                    borderClass="coach-skeleton"
                    containerCss={{ maxWidth: "100%" }}
                  />
                )
              )}
              {agentProfileError && (
                <Box p={3} color={Colors.WHITE}>
                  Something went wrong. Please refresh your browser and try
                  again.
                </Box>
              )}
            </Box>
            <Media greaterThan={hasSidebar ? "md" : "sm"}>
              <Box width={FLOATING_WIDTH} height={0}>
                <Box
                  css={{
                    position: "sticky",
                    top: "70px",
                    zIndex: 1,
                  }}
                >
                  {bookingCoachBox}
                </Box>
              </Box>
            </Media>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="center"
          px={[3, 4]}
          mb={120}
          pt={4}
        >
          <Box
            width={1}
            maxWidth={PUBLIC_CONTENT_MAX_WIDTH}
            pr={FLOATING_FULL_WIDTH}
          >
            {profile && (
              <>
                <Media lessThan={hasSidebar ? "lg" : "md"}>
                  {bookingCoachBox}
                </Media>
                <CoachProfileVideo profile={profile} />
                <ProfileDetails
                  isProfile={true}
                  profile={profile}
                  isAssignedCoach={isAssignedCoach}
                />
                <ClientComments
                  profile={profile}
                  my={0}
                  enableRatingMenu={
                    (currentUser &&
                      profile.user &&
                      currentUser.id == profile.user.id) ||
                    currentUser?.isStaff ||
                    currentUser?.isSuperuser
                  }
                />
                <div className="mt-8">
                  <Specialties
                    coachSlug={profile.coachSlug}
                    specialties={specialties}
                    asCoach={false}
                    showLinks={true}
                    isAssignedCoach={isAssignedCoach}
                    coachProducts={coachProducts}
                  />
                </div>
              </>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  );
}

function AuthCoachProfilePage() {
  const currentUser = useCurrentUser();
  const { data, loading } = useGetAvailableCoachesQuery({
    skip: !currentUser,
  });

  return (
    <>
      <Nav />
      <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
        {loading ? (
          <Box mt={4} className={Classes.SKELETON} height={100} />
        ) : (
          <CoachProfilePage
            isAssignedCoach={
              data?.availableCoaches.some(
                (coach) => coach.id === currentUser?.id,
              ) || false
            }
            hasSidebar={true}
          />
        )}
      </Box>
    </>
  );
}

export function CoachPage() {
  const { currentSessionLink } = useCurrentSession();
  const workspaceSlug = useWorkspaceSlug();
  const currentUser = useCurrentUser();

  if (import.meta.env.SSR && (workspaceSlug || currentUser)) {
    return null;
  }

  if (!import.meta.env.SSR && (currentSessionLink || workspaceSlug)) {
    return (
      <Authenticate maxWidth="100%" layout="full">
        <AuthCoachProfilePage />
      </Authenticate>
    );
  }

  // Only support SSR for Public Coach Profile with no workspace
  return (
    <PageContainer maxWidth="100%">
      <CoachProfilePage isAssignedCoach={false} />
    </PageContainer>
  );
}
